import Helpers from './../helpers';
import Prebid from './prebid';

export default class Targeting {

  /**
   * Processes the ad slot divs on page and for each one sets up the targeting data
   *
   * @param adSlotDivs                - the on page ad slot divs
   * @param responsiveSlotPlacements  - Prebid responsive slots placement config
   * @param responsiveSlotDefinitions - Prebid responsive slot definitions config
   * @returns {Promise<[]>}           - an array of ad slot objects
   */
  static async setDefaults( adSlotDivs, responsiveSlotPlacements, responsiveSlotDefinitions ) {
    let position = 1;
    const slots  = [];

    for ( let i = 0; i < adSlotDivs.length; i++ ) {
      // Define adSlot Data object
      let adSlot = {
        id: adSlotDivs[i].getAttribute( 'id' ),
        zone: ADVERTISING_PAGE_DATA.zone,
        type: this.getSlotType( adSlotDivs[i] ),
        offPage: this.isOffPageSlot( adSlotDivs[i] )
      };

      // Set a random id if one hasn't already been set on the element
      adSlot = await this.setAdSlotID( adSlot, adSlotDivs[i] );

      // Set the ad sizes for legacy slots
      adSlot = await this.setLegacyAdSlotSizes( adSlot, adSlotDivs[i] );

      // Assign the responsive mapping rules to non legacy slots
      adSlot = await this.setResponsiveMappings( adSlot, responsiveSlotDefinitions );

      // Calculate Allowed ad sizes based on current viewport (these are the sizes of ads we will bid for)
      adSlot = await this.setAdSlotBidSizes( adSlot );

      // Set relevant bidder data
      if ( adSlot.offPage === false ) {
        adSlot.bidders = await Prebid.getAllBidders( adSlot.bidSizes.strings, responsiveSlotPlacements );
      }

      adSlot.noMobile = this.isNoMobileSlot( adSlot.type );

      // For each visible advert assign a position number for targeting, Ignoring legacy 1x1, 2x1 and 2x2 slots
      if ( adSlot.offPage === false ) {
        // Additionally, Ignore slots marked no-mobile if on mobile
        if ( adSlot.noMobile === false ) {
          adSlot.position = position;
          adSlotDivs[i].setAttribute( 'data-ad-pos', position );
          position++;
        }
      }

      // Process legacy targeting values (these are defined on the slot div itself)
      let values = adSlotDivs[i].getAttribute( 'data-ad-targeting' );

      if ( values ) {

        adSlot.legacyTargeting = [];
        values                 = values.split( '|' );

        for ( let ii = 0; ii < values.length; ++ii ) {
          let keyValueArray = values[ii].split( '=' );
          adSlot.legacyTargeting.push( {
            key: keyValueArray[0],
            value: (keyValueArray[1].indexOf( ',' ) === -1 ? keyValueArray[1] : keyValueArray[1].split( ',' ))
          } );
        }
      }

      slots.push( adSlot );
    }

    return slots;
  }

  /**
   * Sets a random ID to the ad slot if one hasn't already been defined
   *
   * @param adSlot              - the ad slot object
   * @param adSlotDiv           - the ad slot element
   * @returns {Promise<{id}|*>} - the augmented ad slot object
   */
  static async setAdSlotID( adSlot, adSlotDiv ) {
    if ( ! adSlot.id ) {
      adSlot.id = 'dfp-js-' + Math.random().toString( 36 ).substr( 2, 9 );
      // Set the id on the element so gtm knows which element to populate with an ad
      adSlotDiv.setAttribute( 'id', adSlot.id );
    }
    return adSlot;
  }

  /**
   * Reads the legacy slot size definitions from the element and assigns them to the ad slot object definition
   *
   * @param adSlot         - the ad slot object
   * @param adSlotDiv      - the ad slot element
   * @returns {Promise<*>} - the augmented ad slot object
   */
  static async setLegacyAdSlotSizes( adSlot, adSlotDiv ) {
    if ( adSlot.type === 'legacy' ) {
      adSlot.sizes = [];

      let sizes = adSlotDiv.getAttribute( 'data-ad-sizes' );

      // set a default if element has none defined
      if ( ! sizes ) {
        sizes = [ [ 250, 300 ] ];
      }

      // push sizes to adSlot object in expected array format
      sizes = sizes.split( ',' );

      for ( let i = 0; i < sizes.length; i++ ) {
        const wh = sizes[i].split( 'x' );
        adSlot.sizes.push( [ parseInt( wh[0] ), parseInt( wh[1] ) ] );
      }
    }

    return adSlot;
  }

  /**
   * Assigns the relevant responsive mapping definition to the slot object
   *
   * @param adSlot         - the ad slot object
   * @param definitions    - Prebid responsive slot definitions config
   * @returns {Promise<*>} - the augmented ad slot object
   */
  static async setResponsiveMappings( adSlot, definitions ) {
    // get the responsive mappings
    const mappings = await this.getResponsiveMappings( definitions );
    const keys     = Object.keys( mappings );

    if ( adSlot.type !== 'legacy' && adSlot.type !== 'fluid-native' ) {
      for ( let i = 0; i < keys.length; i++ ) {
        if ( keys[i] === adSlot.type ) {
          adSlot.mappings = mappings[keys[i]];
        }
      }
    }

    return adSlot;
  }

  /**
   * Sets the relevant bid sizes on the ad slot object in both string and array format to simplify processing
   *
   * @param adSlot            - the ad slot object
   * @returns {Promise<{}|*>} - the augmented ad slot object
   */
  static async setAdSlotBidSizes( adSlot ) {

    // don't process off page slots
    if ( adSlot.offPage ) {
      return adSlot;
    }

    // define fluid native sizes (1x1 indicates fluid)
    if ( adSlot.type === 'fluid-native' ) {
      adSlot.bidSizes = {
        strings: [ '1x1' ],
        arrays: [ [ 1, 1 ] ]
      };
      return adSlot;
    }

    adSlot.bidSizes = {strings: [], arrays: []};

    if ( adSlot.type === 'legacy' ) {
      for ( let ii = 0; ii < adSlot.sizes.length; ++ii ) {
        adSlot.bidSizes.strings.push( adSlot.sizes[ii].join( 'x' ) );
        adSlot.bidSizes.arrays.push( adSlot.sizes[ii] );
      }
    }

    if ( adSlot.type !== 'legacy' ) {
      const viewport = Helpers.getViewportDimentions();

      const bidSizes = await this.getAdSlotBidSizes( viewport, adSlot.mappings );

      for ( let ii = 0; ii < bidSizes.length; ii++ ) {
        adSlot.bidSizes.strings.push( bidSizes[ii] );
        adSlot.bidSizes.arrays.push( bidSizes[ii].split( 'x' ).map( Number ) );
      }
    }

    return adSlot;
  }

  /**
   * Calculates the allowed bid sizes based on the current viewport height and width
   *
   * @param viewport - the current viewport size
   * @param adSizes  - the allowed ad sizes based on the responsive mapping definitions
   * @returns {Promise<*[]>}
   */
  static async getAdSlotBidSizes( viewport, adSizes ) {
    const allowedSizes = [];

    for ( let i = 0; i < adSizes.length; i++ ) {
      if ( adSizes[i][0][0] < viewport.width && (adSizes[i][0][1] !== 0 || adSizes[i][0][1] < viewport.height) ) {
        const sizes = adSizes[i][1];

        if ( sizes.length ) {
          for ( let ii = 0; ii < sizes.length; ii++ ) {
            allowedSizes.push( sizes[ii].join( 'x' ) );
          }
        }

        break;
      }
    }

    return allowedSizes.filter( Helpers.onlyUnique );
  }

  /**
   * Processes the responsive size mapping into expected array format
   *
   * @param definitions     - Prebid responsive slot definitions config
   * @returns {Promise<{}>} - the mappings
   */
  static async getResponsiveMappings( definitions ) {
    const mappings = {};

    for ( let i = 0; i < definitions.length; i++ ) {
      const sizes = definitions[i].sizeMappings;

      mappings[definitions[i].name] = [];

      for ( let ii = 0; ii < sizes.length; ii++ ) {
        let mapping = [];
        mapping     = [ sizes[ii].viewport, sizes[ii].adSizes ];
        mappings[definitions[i].name].push( mapping );
      }
    }

    return mappings;
  }

  /**
   * Gets the slot type, defaulting to legacy if it's not defined
   *
   * @param slot         - the ad slot element
   * @returns {string|*} - the ad slot type
   */
  static getSlotType( slot ) {
    const type = slot.getAttribute( 'data-slot-type' );

    if ( type ) {
      return type;
    } else {
      return 'legacy';
    }
  }

  /**
   * Checks whether a slot is considered to be off page or not
   *
   * @param slot - the ad slot element
   * @returns {boolean}
   */
  static isOffPageSlot( slot ) {
    const type   = this.getSlotType( slot );
    const sizes  = slot.getAttribute( 'data-ad-sizes' );
    const ignore = [ '1x1', '2x1', '2x2' ];

    if ( type === 'legacy' && ignore.indexOf( sizes ) > -1 ) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Checks whether a slot is defined as "no-mobile" and if we are on mobile
   *
   * @param type - the slot type
   * @returns {boolean}
   */
  static isNoMobileSlot( type ) {
    if ( screen.width < 767 && type.includes( 'no-mobile' ) ) {
      return true;
    } else {
      return false;
    }
  }

}